import React, { useEffect, useState } from 'react';
import { CameraIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { userProfileAPI } from '../services/api';

export function isImageFile(file) {
  return Boolean(file?.type && file.type.startsWith('image/'));
}

function getInitials(user) {
  const first = user?.firstName?.[0] || '';
  const last = user?.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || 'U';
}

function profileToPayload(profile, profilePhotoUrl) {
  return {
    profilePhotoUrl,
    personalEmail: profile?.personalEmail || null,
    personalMobileNumber: profile?.personalMobileNumber || null,
    officeMobileNumber: profile?.officeMobileNumber || null,
    linkedinUrl: profile?.linkedinUrl || null,
    gender: profile?.gender || null,
    dateOfBirth: profile?.dateOfBirth || null,
    nationality: profile?.nationality || null,
    fatherName: profile?.fatherName || null,
    bloodGroup: profile?.bloodGroup || null,
    languagesSpoken: Array.isArray(profile?.languagesSpoken) ? profile.languagesSpoken : null,
    addresses: profile?.addresses,
    emergencyContacts: profile?.emergencyContacts,
  };
}

export default function UserProfilePhotoUploadModal({
  isOpen,
  onClose,
  user,
  onSaved,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(user?.profilePhotoUrl || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setSelectedFile(null);
    setPreviewUrl(user?.profilePhotoUrl || '');
    setError('');

    return undefined;
  }, [isOpen, user?.profilePhotoUrl]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  if (!isOpen) {
    return null;
  }

  const handleFileChange = (file) => {
    setError('');

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(user?.profilePhotoUrl || '');
      return;
    }

    if (!isImageFile(file)) {
      setSelectedFile(null);
      setError('Please upload an image file.');
      return;
    }

    setSelectedFile(file);
  };

  const uploadPhoto = async (file) => {
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('file', file);
    form.append('folder', 'profile-photos');
    const response = await axios.post('/api/uploads/direct', form, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.publicUrl;
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setError('Please choose an image to upload.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const publicUrl = await uploadPhoto(selectedFile);

      if (!publicUrl) {
        throw new Error('No profile photo URL returned');
      }

      const profileResponse = await userProfileAPI.getProfile();
      const currentProfile = profileResponse.data?.user || user || {};
      const updateResponse = await userProfileAPI.updateProfile(profileToPayload(currentProfile, publicUrl));

      if (typeof onSaved === 'function') {
        onSaved(updateResponse.data?.user || { ...(user || {}), profilePhotoUrl: publicUrl });
      }

      onClose();
    } catch (saveError) {
      console.error('Profile photo update failed:', saveError);
      setError(saveError.message || 'Failed to update profile photo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-slate-950/70 p-4 backdrop-blur-sm sm:items-center">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-lg font-black text-slate-950">Update profile photo</h3>
            <p className="mt-0.5 text-sm text-slate-500">Upload a clear image for your dashboard.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close profile photo upload"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div className="flex justify-center">
            <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-[28px] bg-slate-100 text-xl font-black text-slate-700 ring-1 ring-slate-200">
              {previewUrl ? (
                <img
                  data-testid="profile-photo-preview"
                  src={previewUrl}
                  alt="Profile preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                getInitials(user)
              )}
            </div>
          </div>

          <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 px-4 py-5 text-center transition hover:border-blue-400 hover:bg-blue-100">
            <PhotoIcon className="h-7 w-7 text-blue-600" />
            <span className="mt-2 text-sm font-bold text-slate-900">
              {selectedFile ? selectedFile.name : 'Choose image'}
            </span>
            <span className="mt-1 text-xs text-slate-500">PNG, JPG, or WEBP</span>
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={saving}
              onChange={(event) => handleFileChange(event.target.files?.[0])}
            />
          </label>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-60"
          >
            <CameraIcon className="h-4 w-4" />
            {saving ? 'Uploading...' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
